import type { FunctionComponent } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useParams } from "wouter-preact";
import HeaderNav from "../../components/header_nav/header_nav.tsx";
import { useTranslator } from "../../hooks/i18n.ts";
import { navigate } from "../../router_utils.ts";
import { requestGet } from "../../utils/request.ts";
import { useToast } from "../../components/toast/toast.tsx";

type PreviewCameraInfo = {
    camera_name: string;
    camera_id: number;
    photo_count: number;
    photo_name: string;
    photo_time: number;
    photo_big_url: string;
    photo_small_url: string;
};

type PreviewOverviewResponse = {
    code: number;
    data: PreviewCameraInfo[];
};

export const PreviewOverviewPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const params = useParams();
    const link = params["link"] ?? "";
    const [isBusy, setIsBusy] = useState(false);
    const [cameras, setCameras] = useState([] as PreviewCameraInfo[]);

    useEffect(() => {
        document.title = t({ zh: "预览", en: "Preview" });
    }, [t]);

    const load = useMemo(() => async () => {
        if (!link) return;
        try {
            setIsBusy(true);
            const resp = await requestGet<PreviewOverviewResponse>("/api/preview_overview", { link });
            setCameras(resp.data ?? []);
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [link, toast]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div>
            <HeaderNav title={t({ zh: "预览", en: "Preview" })} />
            <div class="container farpov_top_container">
                <button type="button" aria-busy={isBusy} disabled={isBusy} onClick={load}>
                    {t({ zh: "刷新", en: "Refresh" })}
                </button>
                <hr />
                <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 1rem;">
                    {cameras.map((cam) => (
                        <article key={cam.camera_id}>
                            <header style="display: flex; justify-content: space-between; align-items: center;">
                                <strong>{cam.camera_name}</strong>
                                <small>{t({ zh: "照片", en: "Pic" })}: {cam.photo_count}</small>
                            </header>
                            <div style="aspect-ratio: 4 / 3; overflow: hidden; border-radius: var(--pico-border-radius); display: flex; align-items: center; justify-content: center;">
                                {cam.photo_small_url ? (
                                    <img src={cam.photo_small_url} style="width: 100%; height: 100%; object-fit: contain;" />
                                ) : (
                                    <div style="width: 100%; height: 100%; border: 1px dashed var(--pico-muted-border-color); border-radius: var(--pico-border-radius); display: flex; align-items: center; justify-content: center;">
                                        {t({ zh: "暂无照片", en: "No photos yet" })}
                                    </div>
                                )}
                            </div>
                            <div style="margin-top: 0.5rem;">
                                <small>
                                    {t({ zh: "最近拍摄", en: "Last Shot" })}:{" "}
                                    {cam.photo_time > 0 ? new Date(cam.photo_time * 1000).toLocaleString() : "-"}
                                </small>
                            </div>
                            <footer>
                                <button type="button" class="secondary" onClick={() => navigate(`/preview/${encodeURIComponent(link)}/photos/${cam.camera_id}`, { replace: false })}>
                                    {t({ zh: "查看照片", en: "View Photos" })}
                                </button>
                            </footer>
                        </article>
                    ))}
                </div>
                {(!isBusy && cameras.length <= 0) ? (
                    <div>{t({ zh: "没有可预览的相机。", en: "No cameras." })}</div>
                ) : null}
            </div>
        </div>
    );
};

export default PreviewOverviewPage;
